// The resources a conversation INVOLVES — the right panel's Files overview and
// its teleport roster (D5). Pure: a fold over the already-loaded transcript plus
// the conversation's grounding, resolved against the live library.
//
// Three ways a resource enters a conversation, each read from where it is
// already recorded:
//   • attached  — grounded into its Context (`attachedIds`: durable context,
//                 draft pills, a nascent chat's pending grounding) or
//                 @-referenced in a sent message (inline in its text,
//                 `mentions` live, `referencedIds` after a reload);
//   • read      — a tool step that touched it (read a source, cited one,
//                 exported a package);
//   • created   — a material/export card in the transcript, or what a
//                 planning step made (a course root and its parts).
//
// Ids resolve through `resolve` (the library index), so titles are rename-fresh
// and a deleted resource drops out. First appearance wins the order; a resource
// that entered several ways keeps the strongest (created > attached > read),
// which the right panel's home sorts on (rightPanel/home.ts).

import * as Core from "@bilda/core";
import type { ChatEntry, ToolCallDetail } from "../../components/Chat/types.js";
import type { ResourceRef } from "../../components/Navigation/types.js";
import { materialIdOf } from "./conversationMaterial.js";

// How a resource entered the conversation.
type Involvement = "attached" | "read" | "created";

// `partOf` names the material this one is a part of (a `plan_parts` step
// planned it, or the library knows it): the panel lists it under that root.
export type InvolvedResource = {
    readonly ref: ResourceRef;
    readonly involvement: Involvement;
    readonly partOf?: string;
};

const STRENGTH: Record<Involvement, number> = {
    read: 0,
    attached: 1,
    created: 2,
};

type Mention = {
    readonly id: string;
    readonly involvement: Involvement;
    readonly partOf?: string;
};

const as =
    (involvement: Involvement) =>
    (id: string): Mention => ({ id, involvement });

// What one completed step involves. A step reads what it touched, except what
// it planned: a course root (`plan_course`, D55 S6) and its parts
// (`plan_parts`, bd-lya) are made here.
const detailMentions = (
    toolName: string,
    detail: ToolCallDetail,
): readonly Mention[] => {
    switch (detail.type) {
        case "resource_touched":
            return [
                as(toolName === "plan_course" ? "created" : "read")(
                    detail.resource.id,
                ),
            ];
        case "parts_planned":
            return [
                as("created")(detail.root.id),
                ...detail.parts.map(
                    (p): Mention => ({
                        id: p.id,
                        involvement: "created",
                        partOf: detail.root.id,
                    }),
                ),
            ];
        case "citation_added":
            return [as("read")(detail.source.id)];
        case "package_exported":
            return [as("read")(detail.package.id)];
        case "source_still_processing":
            return [as("read")(detail.source.id)];
        case "recorded_claim":
            return detail.backing.map((b) => as("read")(b.source.id));
        // A listing surveys the library; it involves none of what it lists.
        case "resources_listed":
            return [];
    }
};

const entryMentions = (entry: ChatEntry): readonly Mention[] => {
    switch (entry.type) {
        case "author":
            return [
                ...Core.ProseLink.mentionedResourceIds(entry.text),
                ...(entry.mentions ?? []).map((m) => m.id),
                ...(entry.referencedIds ?? []),
            ].map(as("attached"));
        case "agent_activity":
            return entry.steps.flatMap((step) =>
                step.type === "tool_done" &&
                step.outcome.type === "completed" &&
                step.outcome.detail !== undefined
                    ? detailMentions(step.name, step.outcome.detail)
                    : [],
            );
        case "material":
        case "material_building": {
            const id = materialIdOf(entry);
            return id === undefined ? [] : [as("created")(id)];
        }
        case "export":
            return [as("created")(entry.exportId)];
        case "agent":
        case "proposal":
        case "plan":
        case "system":
        case "information_request":
        case "information_request_pending":
        case "information_submission":
        case "action_offer":
        case "unknown":
            return [];
    }
};

export const conversationResources = (input: {
    readonly attachedIds: readonly string[];
    readonly entries: readonly ChatEntry[];
    readonly resolve: (id: string) => ResourceRef | undefined;
}): readonly InvolvedResource[] => {
    const mentions = [
        ...input.attachedIds.map(as("attached")),
        ...input.entries.flatMap(entryMentions),
    ];
    const ids = mentions.map((m) => m.id);
    const strongest = (id: string): Involvement =>
        mentions
            .filter((m) => m.id === id)
            .reduce<Involvement>(
                (best, m) =>
                    STRENGTH[m.involvement] > STRENGTH[best]
                        ? m.involvement
                        : best,
                "read",
            );
    return ids
        .filter((id, i) => ids.indexOf(id) === i)
        .flatMap((id) => {
            const ref = input.resolve(id);
            // What a planning step says, else the library's own index (a
            // part planned before `plan_parts` named its parts, or one
            // only read here).
            const partOf =
                mentions.find((m) => m.id === id && m.partOf !== undefined)
                    ?.partOf ??
                (ref?.type === "material" ? ref.partOf?.rootId : undefined);
            return ref === undefined
                ? []
                : [
                      partOf === undefined
                          ? { ref, involvement: strongest(id) }
                          : { ref, involvement: strongest(id), partOf },
                  ];
        });
};
